import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { SelectPagination, Button, Text, Modal, Heading } from "@foris/avocado-suite";
import { IParams } from "@models/IParams";
import { ProgrammingStructure, Query } from "@models/ISchema";
import { PROGRAMMING_STRUCTURES_QUERY } from "@services/collections/programming-structures/graphql/programmingStructures.query";
import {
  adaptStructureToSelectOption,
  injectAppliedStructureOption,
} from "@services/collections/programming-edit/programmingEditAdapters";
import { AppContext } from "../../context/EditSessionsContext";
import {
  useProgrammingEditStore,
  applyStructureSelection,
  resetProgrammingEditStore,
  StructureSelectOption,
} from "@context/useProgrammingEditStore";
import css from "./programmingActionBar.module.scss";

interface ProgrammingActionBarProps {
  onStructureChange?: (structureId: string | null) => void;
  onReset?: () => void;
}

const ProgrammingActionBar: React.FC<ProgrammingActionBarProps> = ({
  onStructureChange,
  onReset,
}) => {
  const { t } = useTranslation();
  const { scenario: scenarioId }: IParams = useParams();
  const client = useApolloClient();
  const { state } = useContext(AppContext);
  const linkInfo = state?.link?.info;

  const selectedStructureOption = useProgrammingEditStore(s => s.selectedStructureOption);
  const hasChanges = useProgrammingEditStore(s => s.hasChanges);

  const structuresCacheRef = useRef<Map<string, ProgrammingStructure>>(new Map());
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pendingOption, setPendingOption] = useState<StructureSelectOption | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const subjectCodes = useMemo(
    () => linkInfo?.groups?.map(g => g?.subject?.code).filter(Boolean) ?? [],
    [linkInfo],
  );

  const campusCodes = useMemo(() => {
    const code = linkInfo?.course?.curriculum?.program?.campus?.code;
    return code ? [code] : [];
  }, [linkInfo]);

  // The structure currently applied to the link. Read it as a stable reference so
  // `loadOptions` depends only on what it consumes (not the whole `linkInfo` object,
  // whose identity changes on any reducer update and would needlessly invalidate it).
  const appliedStructure = linkInfo?.programmingStructure;

  const loadOptions = useCallback(
    async (term: string) => {
      let currentPage = 1;
      if (term === searchTerm) {
        currentPage = page;
        setPage(prev => prev + 1);
      } else {
        setSearchTerm(term);
        setPage(2);
      }

      try {
        const { data } = await client.query<Query>({
          query: PROGRAMMING_STRUCTURES_QUERY,
          variables: {
            scenarioId,
            filter: {
              pagination: { page: currentPage, size: 10 },
              orderBy: [{ field: "createdAt", direction: "DESC" }],
              fields: {
                ...(subjectCodes.length && { subjectCodes: { in: subjectCodes } }),
                ...(campusCodes.length && { campusCodes: { in: campusCodes } }),
                isPublished: { is: true },
                isVisible: { is: true },
                ...(term ? { fullText: term } : {}),
              },
            },
          },
        });

        const items = data?.cube?.programmingStructures?.items ?? [];
        const pageInfo = data?.cube?.programmingStructures?.pageInfo;

        items.forEach(s => structuresCacheRef.current.set(s.id, s));

        const baseOptions = items.map(adaptStructureToSelectOption);

        // Keep the link's currently applied structure selectable (CHS-2632, AC5)
        // even when the backend's filtered/paginated query omits it.
        // Caching it makes it resolvable to its parts when the user re-selects it.
        if (appliedStructure) {
          structuresCacheRef.current.set(appliedStructure.id, appliedStructure);
        }

        const options = injectAppliedStructureOption(baseOptions, appliedStructure, {
          page: currentPage,
          term,
        });

        return {
          options,
          hasMore: pageInfo?.hasNextPage ?? false,
          additional: { page: currentPage },
        };
      } catch (error) {
        console.error("[ProgrammingActionBar] loadOptions error:", error);
        return { options: [], hasMore: false };
      }
    },
    [client, scenarioId, subjectCodes, campusCodes, searchTerm, page, appliedStructure],
  );

  const applyStructureChange = (option: StructureSelectOption | null) => {
    const structure = option ? (structuresCacheRef.current.get(option.value) ?? null) : null;
    applyStructureSelection(structure);

    onStructureChange?.(option?.value ?? null);
  };

  const handleStructureChange = (option: StructureSelectOption | null) => {
    const isReplacing =
      !!option && !!selectedStructureOption && option.value !== selectedStructureOption.value;

    if (isReplacing && hasChanges) {
      setPendingOption(option);
      setShowConfirmModal(true);
      return;
    }

    applyStructureChange(option);
  };

  const handleConfirmStructureChange = () => {
    setShowConfirmModal(false);
    applyStructureChange(pendingOption);
    setPendingOption(null);
  };

  const handleCancelStructureChange = () => {
    setShowConfirmModal(false);
    setPendingOption(null);
  };

  const handleReset = () => {
    resetProgrammingEditStore();
    setSearchTerm("");
    setPage(1);
    onReset?.();
  };

  return (
    <div className={css.programmingActionBar}>
      <SelectPagination
        className={css.programmingActionBar_select}
        placeholder={t("programming-edit.action-bar.select-placeholder")}
        loadOptions={loadOptions}
        value={selectedStructureOption}
        onChange={handleStructureChange}
        isOptionDisabled={(data: any) => {
          const programmingStructure = data?.self as ProgrammingStructure;
          const isLinkStructure =
            programmingStructure?.id === state?.link?.info?.programmingStructure?.id;

          return (
            programmingStructure?.totalBlockDuration !== state?.link?.info?.totalBlocks &&
            !isLinkStructure
          );
        }}
        isClearable
        formatOptionLabel={(data: any) => {
          const programmingStructure = data?.self as ProgrammingStructure;

          return (
            <div className={css.programmingStructureOption}>
              <Text type="sm" className={css.programmingStructureOption_label}>
                {programmingStructure?.name} ({programmingStructure?.code})
              </Text>

              <div className={css.programmingStructureOption_description}>
                <Text type="xs" className={css.programmingStructureOption_descriptionItem}>
                  {t("programming-edit.action-bar.total-blocks")}{" "}
                  <span className={css.programmingStructureOption_descriptionValue}>
                    {programmingStructure?.totalBlockDuration ?? "-"}
                  </span>
                </Text>{" "}
                |{" "}
                <Text type="xs" className={css.programmingStructureOption_descriptionItem}>
                  {t("programming-edit.action-bar.structure-type")}{" "}
                  <span className={css.programmingStructureOption_descriptionValue}>
                    {programmingStructure?.source ?? "-"}
                  </span>
                </Text>
              </div>
            </div>
          );
        }}
      />

      {!!selectedStructureOption && (
        <Button
          className={css.programmingActionBar_resetBtn}
          variant="ghost"
          size="sm"
          leftIcon="refresh"
          onClick={handleReset}
        >
          {t("programming-edit.action-bar.reset")}
        </Button>
      )}

      <Modal
        header={<Heading type="h2">{t("programming-edit.change-structure-modal.title")}</Heading>}
        open={showConfirmModal}
        onClose={handleCancelStructureChange}
        actions={
          <>
            <Button variant="secondary" onClick={handleCancelStructureChange}>
              {t("programming-edit.change-structure-modal.cancel")}
            </Button>
            <Button onClick={handleConfirmStructureChange}>
              {t("programming-edit.change-structure-modal.confirm")}
            </Button>
          </>
        }
      >
        <Text>{t("programming-edit.change-structure-modal.description")}</Text>
      </Modal>
    </div>
  );
};

export default ProgrammingActionBar;
